import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Appointment, UpdateAppointmentPayload } from '../../models/appointment.model';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ProfileService } from '../../services/profile.service';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import {
  CustomDatePickerComponent,
  TimeSlot,
} from '../custom-date-picker/custom-date-picker.component';
import { DEFAULT_TIME_SLOTS_8_18 } from '../../shared/constants/time-slots.constant';
import {
  formatDuration as formatDurationUtil,
  mapEmployeesToStaffOptions,
} from '../../shared/utils/appointment.util';
import { getUserTimezone } from '../../shared/utils/date.util';

interface AppointmentEditFormData {
  location: string;
  staff: string;
  date: Date | null;
  duration: { minutes?: number; hours?: number };
}

/**
 * Form component for editing an existing appointment. Builds and manages a reactive form, loads locations and staff for dropdowns, validates that the selected date/time is in the future, converts between internal duration representations and dropdown formats, and emits save or cancel actions.
 * Purpose: Provide an edit UI for appointments, pre-populate fields when an Appointment is supplied, handle location-driven staff loading, validate inputs, and produce a normalized update payload on save.
 * Key methods:
 *   - createForm(): initializes the reactive FormGroup and subscribes to location changes.
 *   - populateForm(): fills the form from the provided appointment and triggers staff loading for the appointment's location.
 *   - loadLocations(): subscribes to profile service locations and maps them for the location dropdown.
 *   - onLocationChange(locationId, callback?): fetches employees for a location, populates staff dropdown and invokes an optional callback.
 *   - convertDurationToDropdownFormat(duration): normalizes duration payloads into dropdown-friendly {hours, minutes} objects.
 *   - convertDropdownFormatToDurationString(durationValue): converts selected dropdown duration into a minutes string for payload.
 *   - formatDuration(duration): returns a human-friendly duration via utility.
 *   - getFieldError(fieldName): returns validation messages for fields.
 *   - onCancel(): emits the cancel event.
 *   - onSave(): validates the form, constructs an UpdateAppointmentPayload and emits the save event; marks fields touched and shows an error message if invalid.
 *   - futureDateTimeValidator(control): custom validator that ensures selected date/time is in the future.
 *   - compareDurationObjects(option, value) / findMatchingDurationOption(durationValue): helpers for duration dropdown comparison and lookup.
 * Lifecycle hooks:
 *   - ngOnInit(): creates the form and initiates loading of locations.
 *   - ngOnChanges(changes): repopulates the form when the input appointment changes.
 * @component
 * @selector app-appointment-edit-form
 * @example
 *   <app-appointment-edit-form [appointment]="selectedAppointment" (cancel)="onCancel()" (save)="onSave($event)"></app-appointment-edit-form>
 * @input {Appointment | null} appointment - The appointment to edit; when provided the component pre-fills the form with appointment data (branch/location, staff, date, duration, etc.).
 * @output {EventEmitter<void>} cancel - Emitted when the user cancels editing; no payload.
 * @output {EventEmitter<UpdateAppointmentPayload>} save - Emitted when the form is valid and saved; payload is a normalized UpdateAppointmentPayload suitable for API updates (includes appointment_time ISO string, duration in minutes string, employee_id, location_id, etc.).
 */
@Component({
  selector: 'app-appointment-edit-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatSelectModule, CustomDatePickerComponent],
  templateUrl: './appointment-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./appointment-edit-form.component.scss'],
})
export class AppointmentEditFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private employeesRequestLocationId: string | null = null;
  @Input() appointment: Appointment | null = null;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<UpdateAppointmentPayload>();

  appointmentForm!: FormGroup;
  locations: { label: string; value: string }[] = [];
  staffOptions: { label: string; value: string }[] = [];
  locationDetails: any = null;

  durationOptions = [
    { label: '30 Minutes', value: { minutes: 30 } },
    { label: '1 Hour', value: { hours: 1 } },
    { label: '1 Hour 30 Minutes', value: { hours: 1, minutes: 30 } },
    { label: '2 Hours', value: { hours: 2 } },
    { label: '2 Hours 30 Minutes', value: { hours: 2, minutes: 30 } },
    { label: '3 Hours', value: { hours: 3 } },
    { label: '3 Hours 30 Minutes', value: { hours: 3, minutes: 30 } },
    { label: '4 Hours', value: { hours: 4 } },
    { label: '4 Hours 30 Minutes', value: { hours: 4, minutes: 30 } },
    { label: '5 Hours', value: { hours: 5 } },
    { label: '5 Hours 30 Minutes', value: { hours: 5, minutes: 30 } },
    { label: '6 Hours', value: { hours: 6 } },
    { label: '6 Hours 30 Minutes', value: { hours: 6, minutes: 30 } },
    { label: '7 Hours', value: { hours: 7 } },
    { label: '7 Hours 30 Minutes', value: { hours: 7, minutes: 30 } },
    { label: '8 Hours', value: { hours: 8 } },
  ];

  // Custom time slots for the date picker (08:00–18:00)
  timeSlots: TimeSlot[] = [...DEFAULT_TIME_SLOTS_8_18];

  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private apiService: ApiService,
    private messageService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  // Getter to check if all form fields are filled
  /**
   * Determines whether the appointment form is complete and valid.
   * @example
   * isFormComplete()
   * true
   * @returns {boolean} True if appointmentForm exists, has non-empty location, staff, date, and duration fields and the form is valid; otherwise false.
   */
  get isFormComplete(): boolean {
    if (!this.appointmentForm) return false;

    const formValue = this.appointmentForm.value;
    return Boolean(
      formValue.location &&
      formValue.staff &&
      formValue.date &&
      formValue.duration &&
      this.appointmentForm.valid
    );
  }

  ngOnInit(): void {
    this.createForm();
    this.loadLocations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['appointment'] && this.appointment && this.appointmentForm) {
      this.populateForm();
    }
  }

  /**
   * Initialize and configure the appointment reactive form and subscribe to location changes to load staff.
   * @example
   * createForm()
   * void
   * @returns {void} Creates the form group with controls (location, staff, date, duration), attaches validators, and sets up a subscription to update staff options when the location control value changes; does not return a value.
   */
  createForm(): void {
    this.appointmentForm = this.fb.group({
      location: ['', [Validators.required]],
      staff: ['', [Validators.required]],
      date: [null, [Validators.required, this.futureDateTimeValidator]],
      duration: ['', [Validators.required]],
    });

    // Listen for location changes to load staff
    this.appointmentForm
      .get('location')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(locationId => {
        if (locationId) {
          this.onLocationChange(locationId);
        } else {
          // Invalidate in-flight staff/location fetches for the previous location.
          this.employeesRequestLocationId = null;
          this.staffOptions = [];
          this.locationDetails = null;
          this.appointmentForm.patchValue({ staff: '' }, { emitEvent: false });
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Populate the appointment edit form from this.appointment, converting duration and time, setting location, date and duration fields, and loading/setting staff when appropriate.
   * @example
   * opulateForm()
   * void
   * @returns {void} Updates the appointmentForm (patches location, date, duration and staff) and may trigger asynchronous staff loading; does not return a value.
   */
  populateForm(): void {
    if (!this.appointment) return;

    // Find location by name
    const location = this.locations.find(loc => loc.label === this.appointment?.branch);
    const locationId = location?.value || '';

    // Convert duration to format expected by dropdown - find matching option from durationOptions
    const durationValue = this.convertDurationToDropdownFormat(this.appointment?.duration);
    const matchingDurationOption = this.findMatchingDurationOption(durationValue);

    // Convert appointment time to Date object
    const appointmentDate = this.appointment?.appointment_time
      ? new Date(this.appointment.appointment_time)
      : null;

    // Prefill staff so the complete-details banner does not flash while employees API loads.
    if (this.appointment.employee_id) {
      this.staffOptions = [
        {
          label: this.appointment.employee_name || 'Staff',
          value: this.appointment.employee_id,
        },
      ];
      this.cdr.markForCheck();
    }

    this.appointmentForm.patchValue(
      {
        location: locationId,
        date: appointmentDate,
        duration: matchingDurationOption ? matchingDurationOption.value : durationValue,
        staff: this.appointment.employee_id || '',
      },
      { emitEvent: false }
    );

    // Load full staff list for the location; keep prefilled staff until then.
    if (locationId) {
      this.onLocationChange(locationId, () => {
        // Callback after staff is loaded - set staff value using employee_id
        if (this.appointment?.employee_id) {
          const staffMember = this.staffOptions.find(
            s => s.value === this.appointment?.employee_id
          );
          if (staffMember) {
            this.appointmentForm.patchValue({ staff: staffMember.value }, { emitEvent: false });
          } else {
            // Fallback: try to match by name if employee_id doesn't match
            const staffMemberByName = this.staffOptions.find(
              s => s.label === this.appointment?.employee_name
            );
            this.appointmentForm.patchValue(
              { staff: staffMemberByName?.value || '' },
              { emitEvent: false }
            );
          }
          this.cdr.markForCheck();
        }
      });
    }
  }

  /**
   * Load location options from the ProfileService into the component and populate the appointment form if an appointment is present.
   * @example
   * loadLocations()
   * // returns void
   * @returns {void} Does not return a value.
   */
  loadLocations(): void {
    this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
      this.locations = locations.map((location: any) => ({
        label: location.branch,
        value: location.id,
      }));
      this.cdr.markForCheck();

      // If we have an appointment, populate the form after locations are loaded
      if (this.appointment) {
        this.populateForm();
      }
    });
    this.profileService.loadLocations();
  }

  /**
   * Handle location change: clears staff dropdown, fetches employees for the given location, updates the form and staff list, and optionally invokes a callback after the staff are loaded.
   * @example
   * nLocationChange('locationId123', () => { console.log('staff updated'); })
   * undefined
   * @param {{string}} {{locationId}} - The ID of the selected location.
   * @param {{() => void}} {{callback}} - Optional callback invoked after the staff list is updated (executed after view update when staff are successfully loaded).
   * @returns {{void}} Returns nothing.
   **/
  onLocationChange(locationId: string, callback?: () => void): void {
    if (locationId) {
      this.employeesRequestLocationId = locationId;

      // User changed location: clear staff. Populate path (callback) keeps prefilled staff.
      if (!callback) {
        this.staffOptions = [];
        this.appointmentForm.patchValue({ staff: '' }, { emitEvent: false });
        this.cdr.markForCheck();
      }

      // Clear stale location details so the date picker doesn't use the
      // previous location's timezone/hours while the new fetch is in-flight.
      this.locationDetails = null;

      this.apiService
        .getEmployeesByLocation(locationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            // Ignore stale responses if another location load started meanwhile.
            if (this.employeesRequestLocationId !== locationId) {
              return;
            }
            if (response.status && Array.isArray(response.data)) {
              this.staffOptions = mapEmployeesToStaffOptions(response.data);
              this.cdr.markForCheck();

              if (callback) {
                callback();
              }
            } else {
              this.staffOptions = [];
              this.notifyError('No staff found for this location');
              this.cdr.markForCheck();
              if (callback) {
                callback();
              }
            }
          },
          error: () => {
            if (this.employeesRequestLocationId !== locationId) {
              return;
            }
            // User location change already cleared staff; populate path keeps prefilled option.
            if (!callback) {
              this.staffOptions = [];
            }
            this.notifyError('Failed to load staff for this location');
            this.cdr.markForCheck();
            if (callback) {
              callback();
            }
          },
        });

      this.profileService
        .getLocationDetails(locationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          if (this.employeesRequestLocationId !== locationId) {
            return;
          }
          this.locationDetails = response.status && response.data ? response.data : null;
          this.cdr.markForCheck();
        });
    } else {
      this.employeesRequestLocationId = null;
      this.staffOptions = [];
      this.locationDetails = null;
      this.cdr.markForCheck();
      if (callback) {
        callback();
      }
    }
  }

  private notifyError(detail: string, summary = 'Error'): void {
    this.messageService.add({ severity: 'error', summary, detail });
  }

  /**
   * Convert a duration input into an object suitable for hours/minutes dropdowns.
   * @example
   * onvertDurationToDropdownFormat({ minutes: 135 })
   * { hours: 2, minutes: 15 }
   * @param {{any}} {{duration}} - The duration to convert; can be falsy, already in {hours, minutes} form, or an object with a minutes number.
   * @returns {{minutes?: number; hours?: number}} Return an object with hours and/or minutes properties, or an empty object if duration is invalid.
   **/
  convertDurationToDropdownFormat(duration: any): { minutes?: number; hours?: number } {
    if (!duration) {
      return {};
    }

    // If duration already has hours and minutes format, return as is
    if (duration.hours !== undefined || duration.minutes !== undefined) {
      return {
        hours: duration.hours,
        minutes: duration.minutes,
      };
    }

    // If duration only has minutes, convert to hours and minutes
    if (typeof duration.minutes === 'number') {
      const totalMinutes = duration.minutes;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return { hours, minutes };
      }
      if (hours > 0) {
        return { hours };
      }
      return { minutes };
    }
    return {};
  }

  convertDropdownFormatToDurationString(durationValue: {
    minutes?: number;
    hours?: number;
  }): string {
    if (!durationValue) {
      return '60 minutes';
    }

    const { hours, minutes } = durationValue;
    const totalMinutes = (hours || 0) * 60 + (minutes || 0);

    return `${totalMinutes} minutes`;
  }

  formatDuration(duration: { minutes?: number; hours?: number } | undefined | null): string {
    return formatDurationUtil(duration);
  }

  get showShopTimezoneWarning(): boolean {
    const shopTimezone = this.locationDetails?.timezone || getUserTimezone();
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !!shopTimezone && !!browserTimezone && shopTimezone !== browserTimezone;
  }

  /**
   * Return a validation error message for a given form field if it has been touched and is invalid.
   * @example
   * etFieldError('appointmentDate')
   * 'Please select a future date and time'
   * @param {string} fieldName - Name of the form control to check for validation errors.
   * @returns {string} Return validation error message if the field has been touched and contains validation errors; otherwise returns an empty string.
   **/
  getFieldError(fieldName: string): string {
    const field = this.appointmentForm.get(fieldName);
    if (field?.errors && field?.touched) {
      if (field.errors['required']) {
        return 'This field is required';
      }
      if (field.errors['pastDateTime']) {
        return 'Please select a future date and time';
      }
    }
    return '';
  }

  onCancel() {
    this.cancel.emit();
  }

  /**
   * Validate the appointment form, construct an UpdateAppointmentPayload and emit it via the save event when valid; otherwise mark controls as touched and show a validation error message.
   * @example
   * nSave()
   * undefined
   * @param {{void}} none - No arguments; method relies on component state (this.appointmentForm, this.appointment).
   * @returns {{void}} Emits the constructed UpdateAppointmentPayload via this.save when the form is valid; otherwise returns nothing.
   **/
  onSave() {
    if (this.appointmentForm.valid && this.appointment) {
      const formValue = this.appointmentForm.value;

      // Create update payload using the specified structure
      const updatePayload: UpdateAppointmentPayload = {
        appointment_status:
          this.appointment.appointment_status === 'pending'
            ? 'confirmed'
            : this.appointment.appointment_status,
        employee_id: formValue.staff,
        customer_id: this.appointment.customer_id,
        vehicle_id: this.appointment.vehicle_id, // vehicle_id is not available in the Appointment interface
        package_id: this.appointment.package_id,
        location_id: formValue.location,
        appointment_time: formValue.date ? formValue.date.toISOString() : '',
        source: this.appointment.source || 'web',
        duration: this.convertDropdownFormatToDurationString(formValue.duration),
      };

      this.save.emit(updatePayload);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.appointmentForm.controls).forEach(key => {
        const control = this.appointmentForm.get(key);
        control?.markAsTouched();
      });

      this.notifyError('Please fill in all required fields correctly.', 'Validation Error');
    }
  }

  /**
   * Ensures the control's date/time value is in the future; returns a validation error if it is in the past.
   * @example
   * futureDateTimeValidator(new FormControl('2000-01-01T10:00'))
   * { pastDateTime: true }
   * @param {AbstractControl} control - Form control whose value is validated as a future date/time.
   * @returns {ValidationErrors | null} Validation error object with key "pastDateTime" if value is not in the future, otherwise null.
   **/
  private futureDateTimeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const selectedDate = new Date(control.value);
    const now = new Date();

    // Check if the selected date/time is in the past
    if (selectedDate <= now) {
      return { pastDateTime: true };
    }

    return null;
  }

  // Method to compare duration objects for dropdown selection
  compareDurationObjects(option: any, value: any): boolean {
    if (!option || !value) return false;
    return option.hours === value.hours && option.minutes === value.minutes;
  }

  // Find matching duration option from durationOptions array
  /**
   * Find the duration option in the component's durationOptions that exactly matches the provided hours and minutes.
   * @example
   * indMatchingDurationOption({ hours: 1, minutes: 30 })
   * { label: '1h 30m', value: { hours: 1, minutes: 30 } }
   * @param {{ minutes?: number; hours?: number }} {{durationValue}} - Duration object with optional hours and minutes to match.
   * @returns {{ label: string; value: {{ minutes?: number; hours?: number }} } | null} The matching option (label and value) if found, otherwise null.
   **/
  findMatchingDurationOption(durationValue: {
    minutes?: number;
    hours?: number;
  }): { label: string; value: { minutes?: number; hours?: number } } | null {
    if (!durationValue || (!durationValue.hours && !durationValue.minutes)) {
      return null;
    }

    // Find the exact matching option
    const matchingOption = this.durationOptions.find(option => {
      const optionValue = option.value;
      const optionHours = optionValue.hours || 0;
      const optionMinutes = optionValue.minutes || 0;
      const valueHours = durationValue.hours || 0;
      const valueMinutes = durationValue.minutes || 0;

      return optionHours === valueHours && optionMinutes === valueMinutes;
    });

    return matchingOption || null;
  }
}
